/**
 * @file formatDuration.ts
 *
 * @description Utility functions for formatting video duration strings
 *
 * @module Utils
 */

/**
 * @name formatDuration
 *
 * @description Converts a duration string (in seconds or other formats) to a readable format with units
 *
 * @param {string | number} duration - Duration in seconds (number) or string format
 *
 * @returns {string} Formatted duration with units (e.g., "1h 23m 45s")
 */
export const formatDuration = (duration: string | number): string => {
  if (duration === undefined || duration === null) return "0h 0m 0s";

  let totalSeconds = 0;

  if (typeof duration === "number") {
    totalSeconds = duration;
  } else if (typeof duration === "string") {
    // If already in HH:MM:SS or MM:SS format, parse to seconds
    if (duration.includes(":")) {
      const parts = duration.split(":").map((p) => parseInt(p, 10));

      if (parts.length === 3) {
        // HH:MM:SS
        totalSeconds = parts[0] * 3600 + parts[1] * 60 + parts[2];
      } else if (parts.length === 2) {
        // MM:SS
        totalSeconds = parts[0] * 60 + parts[1];
      } else {
        return duration;
      }
    } else {
      // Try parsing as total seconds string
      totalSeconds = parseFloat(duration);
    }
  }

  if (isNaN(totalSeconds)) {
    return duration.toString();
  }

  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = Math.floor(totalSeconds % 60);

  return `${hours}h ${minutes}m ${seconds}s`;
};
